import { useCallback } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import { NavMarker } from 'react-rhumb';
import UITextInput from 'UIComponents/input/UITextInput';
import UIButton from 'UIComponents/button/UIButton';
import UIFormLabel from 'UIComponents/form/UIFormLabel';
import UIFlex from 'UIComponents/layout/UIFlex';
import UIBox from 'UIComponents/layout/UIBox';
import Small from 'UIComponents/elements/Small';
import { View } from 'foundations-components/components/View';
import { Tag } from 'foundations-components/transitional/components/Tag';
import { Box } from 'foundations-components/transitional/components/Box';
import SettingsRowLayout from 'settings-ui-lib/components/SettingsRowLayout';
import SettingsRowWrapper from 'settings-ui-lib/components/SettingsRowWrapper';
import { getEmailAddressFromCurrentUserProfile, getPendingEmailAddressFromCurrentUserProfile, getIsScimUser } from '../selectors/ProfileSelector';
import { getFetchProfileRequestStatus } from '../selectors/RequestStatusSelector';
import { anyRequestsPending } from '../utils/requestUtils';
import { cancelPendingEmailAddress as cancelPendingEmailAddressAction, resendPendingConfirmation as resendPendingConfirmationAction } from '../actions/EmailAddressActions';
import { toggleEditEmailAddressModalOpen as toggleEditEmailAddressModalOpenAction } from '../actions/AppActions';
import { allFinishedWithSuccess } from '../utils/requestUtils';
import EditEmailAddress from '../components/EditEmailAddress';
import { getEmailFromAuth } from '../selectors/AuthSelector';
import { getCanEditEmailAddress } from '../selectors/EmailsSelector';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const EmailAddressContainer = ({
  pendingEmailAddress,
  emailAddressFromProfile,
  cancelPendingEmailAddress,
  resendPendingConfirmation,
  fetchEmailAddressesRequestStatus,
  canEditEmailAddress,
  toggleEditEmailAddressModalOpen,
  emailFromAuth,
  isScimUser
}) => {
  const onCancelPending = useCallback(() => {
    cancelPendingEmailAddress(pendingEmailAddress);
  }, [pendingEmailAddress, cancelPendingEmailAddress]);
  const onResendPending = useCallback(() => {
    resendPendingConfirmation(pendingEmailAddress);
  }, [pendingEmailAddress, resendPendingConfirmation]);
  const haveAllFinishedWithSuccess = allFinishedWithSuccess(fetchEmailAddressesRequestStatus);
  const areAnRequestsPending = anyRequestsPending(fetchEmailAddressesRequestStatus);
  const displayEmail = emailAddressFromProfile || emailFromAuth;
  const renderAppleMessage = () => {
    if (displayEmail.includes('@privaterelay.appleid.com')) {
      return /*#__PURE__*/_jsx(FormattedHTMLMessage, {
        message: "userPreferences.help.appleEmailWarning"
      });
    }
    if (displayEmail.includes('@icloud.com')) {
      return /*#__PURE__*/_jsx(FormattedHTMLMessage, {
        message: "userPreferences.help.appleEmailWarningMaybe"
      });
    }
    return null;
  };
  if (areAnRequestsPending) {
    return /*#__PURE__*/_jsx(SettingsRowLayout, {
      title: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "userPreferences.labels.emailAddress"
      }),
      description: /*#__PURE__*/_jsx(Small, {
        use: "help",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "userPreferences.labels.loading"
        })
      })
    });
  }
  return /*#__PURE__*/_jsxs(SettingsRowWrapper, {
    children: [/*#__PURE__*/_jsx(NavMarker, {
      name: "EMAIL_ADDRESSES_LOADED"
    }), /*#__PURE__*/_jsx(View, {
      marginBlockEnd: "200",
      padding: "0",
      children: /*#__PURE__*/_jsx(UIFormLabel, {
        htmlFor: "email-input",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "userPreferences.labels.emailAddress"
        })
      })
    }), /*#__PURE__*/_jsxs(UIFlex, {
      children: [/*#__PURE__*/_jsxs(UIBox, {
        basis: 360,
        children: [/*#__PURE__*/_jsx(UITextInput, {
          id: "email-input",
          defaultValue: displayEmail,
          readOnly: true
        }), canEditEmailAddress && /*#__PURE__*/_jsx(EditEmailAddress, {
          onEditEmailAddress: toggleEditEmailAddressModalOpen,
          isScimUser: isScimUser,
          currentEmail: emailFromAuth
        }), renderAppleMessage()]
      }), haveAllFinishedWithSuccess && pendingEmailAddress && /*#__PURE__*/_jsxs(Box, {
        padding: "0",
        shrink: 0,
        children: [/*#__PURE__*/_jsx(Tag, {
          inline: true,
          use: "warning",
          marginInline: "200",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "userPreferences.labels.pendingValidation"
          })
        }), /*#__PURE__*/_jsx(UIButton, {
          use: "link",
          onClick: onResendPending,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "userPreferences.labels.resend"
          })
        }), /*#__PURE__*/_jsx(View, {
          marginInline: "200",
          display: "inline-block",
          children: "|"
        }), /*#__PURE__*/_jsx(UIButton, {
          use: "link",
          onClick: onCancelPending,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "userPreferences.labels.cancel"
          })
        })]
      })]
    })]
  });
};
EmailAddressContainer.propTypes = {
  cancelPendingEmailAddress: PropTypes.func.isRequired,
  canEditEmailAddress: PropTypes.bool.isRequired,
  emailAddressFromProfile: PropTypes.string.isRequired,
  fetchEmailAddressesRequestStatus: PropTypes.string.isRequired,
  pendingEmailAddress: PropTypes.string,
  resendPendingConfirmation: PropTypes.func.isRequired,
  toggleEditEmailAddressModalOpen: PropTypes.func.isRequired,
  emailFromAuth: PropTypes.string.isRequired,
  isScimUser: PropTypes.bool.isRequired
};
const mapStateToProps = state => {
  return {
    fetchEmailAddressesRequestStatus: getFetchProfileRequestStatus(state),
    emailAddressFromProfile: getEmailAddressFromCurrentUserProfile(state),
    pendingEmailAddress: getPendingEmailAddressFromCurrentUserProfile(state),
    canEditEmailAddress: getCanEditEmailAddress(state),
    emailFromAuth: getEmailFromAuth(state),
    isScimUser: getIsScimUser(state)
  };
};
export default connect(mapStateToProps, {
  cancelPendingEmailAddress: cancelPendingEmailAddressAction,
  resendPendingConfirmation: resendPendingConfirmationAction,
  toggleEditEmailAddressModalOpen: toggleEditEmailAddressModalOpenAction
})(EmailAddressContainer);